
import React, { useRef } from "react";
import { Tables } from "@/integrations/supabase/types";
import MapLoadingOverlay from "./states/MapLoadingOverlay";
import NoReportsMessage from "./states/NoReportsMessage";
import GoogleMapErrorState from "./states/MapErrorState";
import { useCrimeMarkers } from "@/hooks/maps/useCrimeMarkers";
import { useGoogleMap } from "@/hooks/useGoogleMap";

type CrimeReport = Tables<"crime_reports">;

interface GoogleCrimeMapProps {
  crimeReports: CrimeReport[];
  showLast24Hours?: boolean;
  initialLocation?: { lat: number; lng: number } | null;
  isGoogleMapsLoaded: boolean;
  mapError: string | null;
  onUseFallbackMap: () => void;
}

const GoogleCrimeMap: React.FC<GoogleCrimeMapProps> = ({
  crimeReports,
  showLast24Hours = false,
  initialLocation = null,
  isGoogleMapsLoaded,
  mapError,
  onUseFallbackMap
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  
  const { map } = useGoogleMap({ 
    mapRef, 
    initialLocation,
    onLocationSelected: () => {} // Not needed for crime map
  });

  const { hasRecentReports } = useCrimeMarkers({ 
    map, 
    crimeReports, 
    showLast24Hours 
  });

  // If there's a map error, immediately render the error state
  if (mapError) {
    return (
      <GoogleMapErrorState 
        errorMessage={mapError}
        onUseFallbackMap={onUseFallbackMap}
      />
    );
  }

  return (
    <div ref={mapRef} className="w-full h-full">
      {!isGoogleMapsLoaded && <MapLoadingOverlay />}
      
      {isGoogleMapsLoaded && crimeReports.length === 0 && (
        <NoReportsMessage 
          title="No Crime Reports" 
          description="There are no crime reports to display on the map."
        />
      )}
      
      {isGoogleMapsLoaded && showLast24Hours && crimeReports.length > 0 && 
        !hasRecentReports() && (
        <NoReportsMessage 
          title="No Recent Reports" 
          description="There are no crime reports from the last 24 hours."
        />
      )}
    </div>
  );
};

export default GoogleCrimeMap;
